import * as d3 from 'd3-selection'
import * as array from 'd3-array'
import * as shape from 'd3-shape'
import { EventEmitter } from 'events'
import { values } from 'd3-collection'
import yaxis from 'y-axis'
import hover from './lib/hover'
import plot from './lib/plot'
import * as transition from 'd3-transition' // Also does a mixin into d3-selection prototype
import Layout from './lib/layout'
import { TRANSITION_DURATION as DURATION } from 'chart-constants'
import xScaler from 'x-scaler'
import yScaler from 'y-scaler'
import xAxis from 'x-axis'
import ChartDrawAbortError from 'chart-draw-abort-error'
import { dimensions as xAxisDimensions, heights as xAxisHeights } from 'x-axis'

let count = 0

function cloneScale (yScaler, xDimensions, axesConfig, breakdown) {
  let yScale = Object.keys(yScaler).reduce((p, c) => {
    p[c] = yScaler[c].copy()
    p[c]._domain = yScaler[c]._domain
    return p
  }, {})

  if (axesConfig.bottom.labels) {
    let hasTitle = !!breakdown.range.find(r => r.title)
      , hasSubtitle = !!breakdown.range.find(r => r.subtitle)
      , delta = 0

    if (hasTitle) {
      delta += (xDimensions.xAxisTitleHeight +
               xDimensions.gridTopExtraPadding)
    }

    if (hasSubtitle) {
      delta += xDimensions.xAxisSubtitleHeight
    }

    Object.keys(yScale).forEach(axis => {
      let height = yScale[axis].range()[0] - delta
      yScale[axis].range([Math.max(0, height), 0])
    })
  }
  return yScale
}

/*
 * Adjusts the dimensions based on labels on each axis
 */
function adjustDimensions (chart, dimensions, xaxis, scalers) {
  let plotIdx = array.maxIndex(chart.datum().plots, plot => {
      // Use the plot with the longest breakdown title in case we're showing
      // two levels of breakdowns and have titles at the top
      let placeholder = chart.append('text')
                             .attr('class', 'tick')
                             .text(plot.breakdown.title || '')
        , width = placeholder.node().getBBox().width

      placeholder.remove()

      return width
    })
    , plot = chart.datum().plots[plotIdx]
    , axes = chart.append('g')
                  .data([plot]) // Send in stub plot data
                  .attr('class', 'axes')
    , axesConfig = chart.layout.axes

  if (axesConfig.left.labels && axesConfig.right.labels) {
    // Some kind kind of y-labels are set, so we have to adjust

    // Clone the y scalers, since we're going to adjust the range as our best guess of x-axis height
    let yScale = cloneScale(scalers.y, xAxisDimensions, axesConfig, chart.layout.breakdown)
    axes.call(yaxis(chart.layout, scalers.x.groupScale.range()[1], yScale))
        .selectAll('.axis-y')
        .nodes()
        .forEach(function (axis) {
          let _axis = d3.select(axis)
            , width = array.max(_axis.selectAll('text').nodes(), function (node) {
                            return node.getBBox().width
                          })
          if (typeof width === 'number') {
            dimensions.margin[_axis.attr('data-orient')] += width
            dimensions.width -= width

            // Adjust the plot scale with this new width
            // This is needed to determine if the x-axis labels should be rotated
            // based on the new y-axis tick width
            let plotRange = scalers.x.plotScale.range()
            scalers.x.plotScale.range([0, plotRange[1] - width])
          }
        })
  }

  if (axesConfig.bottom.labels) {
    axes.call(xaxis(scalers, chart))

    let heights = xAxisHeights(axes, chart)
      , total = heights.bottom + heights.top

    if (total) {
      dimensions.margin.bottom += heights.bottom
      dimensions.margin.top += heights.top + xAxisDimensions.gridTopExtraPadding // If we're showing the x-axis, we adjust the padding at the top, too
      dimensions.height -= (total + xAxisDimensions.gridTopExtraPadding)
    }
  }

  axes.remove()
}

/*
 * Receives chart data object, which contains the width/height for the chart
 */
function defaultDimensions (data, layout) {
  const margin = 20 // default
  const sideMargin = layout.hasYAxis ? 15 : margin // charts that show y-axis have smaller left/right margins

  let {width, height, padding} = data
    , d = {
      margin: {
        top: margin,
        right: sideMargin,
        bottom: margin,
        left: sideMargin
      },
      padding: {
        serie: {
          inner: .12,
          outer: .2
        },
        group: {
          inner: .17,
          outer: .05
        },
        plot: {
          inner: .15,
          outer: .05
        }
      }
    }

  if (!padding) {
    padding = {}
  }

  // Set the padding levels for scale
  ;['serie', 'group', 'plot'].forEach(pad => {
    if (!padding[pad]) {
      // No specific overrides
      return
    }
    d.padding[pad].inner = typeof padding[pad].inner !== 'undefined' ? padding[pad].inner : d.padding[pad].inner
    d.padding[pad].outer = typeof padding[pad].outer !== 'undefined' ? padding[pad].outer : d.padding[pad].outer
  })

  d.width = Math.max(0, width - d.margin.left - d.margin.right)
  d.height = Math.max(0, height - d.margin.top - d.margin.bottom)

  // Store the container's width/height so we can compare it later for transition purposes
  d._containerWidth = width
  d._containerHeight = height

  // If the container width or height feed us a non number (not displayed), force things to 0
  if (isNaN(d.width)) {
    d.width = 0
  }

  if (isNaN(d.height)) {
    d.height = 0
  }

  return d
}

/*
 * Sets the default axes settings, allowing for incoming overrides
 */
function defaultAxes (data) {
  data.axes = data.axes || {}
  data.axes.left = data.axes.left || {}
  data.axes.right = data.axes.right || {}
  data.axes.bottom = data.axes.bottom || {}
  data.axes.infographic = data.axes.infographic || {}
  data.axes.pie = data.axes.pie || {}
  data.axes.polar = data.axes.polar || {}
  // Set each axis `labels` to `true` if not defined
  ;['left', 'right', 'bottom'].forEach(function (axis) {
    data.axes[axis].labels = data.axes[axis].labels === false ? data.axes[axis].labels : true
  })
}

class Chart extends EventEmitter {

  constructor (container) {
    super()

    this.container = container

    // Set up the defaults, can be overridden through the public methods
    this._xAccessor = d => d.rangeId
    this._yAccessor = d => d.raw

    this._ee = new EventEmitter // Internal event emitter.
    this._xScaler = xScaler
    this._yScaler = yScaler
    this._xaxis = xAxis
    this._dimensions = d => d
    this._hoverTooltipRows = null
    this._hoverContents = null
    this._curve = {
      line: null, // Default jagged line graphs
      area: shape.curveMonotoneX, // Default smooth area graphs
      polar: shape.curveCardinalClosed
    }

  }

  _resetEventListeners () {
    // Clear listeners from previous render
    this._ee.removeAllListeners()

    // When the internal event emitter (i.e. `this._ee`), fires a `chart-event`,
    // we delegate that event to the external chart object
    this._ee.on('chart-event', (...args) => {
      this.emit.apply(this, args)
    })

    // If some internal chart code ever fires a `fresh-redraw`, it means
    // it's bailing on animations. Force a fresh redraw on the chart
    this._ee.on('fresh-redraw', data => {
      this.render(data, {fresh: true})
    })
  }

  render (data, opts = {}) {
    let container = this.container

    this._resetEventListeners()

    if (opts.fresh) {
      // Don't transition. Full chart update.
      // It's best to blow away the SVG so a brand new chart is rendered without
      // animations trying to run
      this.destroyTooltip() // Force a tooltip removal, too
      d3.select(container).selectAll('svg').remove()
    }

    let svg = d3.select(container).selectAll('svg')

    // Check if the chart is in the process of updating from a previous fun
    if (svg.size() && svg.classed('is-updating')) {
      // This seems horribly inefficient. This will send interrupts to
      // all current transitions. If we have `.end()` promises attached,
      // they'll need a `.catch()` for the intercept or an error will log to the console.
      this.destroyTooltip() // To prevent tooltips from lingering if the original svg was destroyed
      d3.select(container).selectAll('*').transition().delay(0)
      d3.select(container).selectAll('svg').remove()
    }

    svg = d3.select(container)
            .selectAll('svg').data([data]) // Bind the data

    let enter = svg.enter()
                   .append('svg')
                     .attr('class', 'chart-svg')
                     .attr('id', function () {
                       this._chartId = 'chart-svg-' + (++count)
                       return this._chartId
                     })
      , _svg = this.svg = enter.merge(svg)
      , isUpdating = _svg.classed('is-updating') // Check if transitions are running

    _svg.classed('is-updating', true) // Set a flag indicating the chart is updating

    defaultAxes(data)

    enter.append('defs') // Used by plots for clip paths
    enter.append('g')
           .attr('class', 'chart')
           .append('g')
             .attr('class', 'axes') // Really just the y-axes (left/right). The x is drawn in the subplot

    let layout = new Layout(data)
      , _dimensions = this._dimensions(defaultDimensions(data, layout))
      , chart = _svg.selectAll('.chart')
                    .data(d => {
                      return [{
                        axes: d.axes,
                        plots: d.plots,
                        tooltip: d.tooltip || {},
                        dimensions: _dimensions
                      }]
                    })
      , _scalers = {
        // Initially build scalers based on full width height
        y: this._yScaler(data, layout, _dimensions)(),
        x: this._xScaler(layout, _dimensions)
      }

    this._chart = chart

    chart.id = _svg.attr('id')

    // Create two transitions, one for the y-axis and one for basic chart updates
    // By default they don't have a duration
    chart.yTransition = transition.transition('y-transition')
                                  .duration(0)
    chart.defaultTransition = transition.transition(`chart-transition-${chart.id}`)
                                        .duration(0)
    chart.layout = layout

    _svg.classed('multi-series', layout.nonAnalyticsSeries(data.plots[0].series).length > 1)

    /*
     * Looks up a property value on the chart's svg node
     */
     // TODO Move this somewhere
    chart.getPropertyValue = (property) => {
      return getComputedStyle(_svg.node()).getPropertyValue(property)
    }

    // Now that we have scalers, we need to temporarily build axes so we know if the scalers' ranges needs adjusting
    adjustDimensions(chart, _dimensions, this._xaxis, _scalers)

    // Now adjust those scalers based on new dimensions...
    // TODO Can probably optimize this by updating a scalers range?
    _scalers.y = this._yScaler(data, layout, _dimensions)()
    _scalers.x = this._xScaler(layout, _dimensions)

    let prevWidth = chart.attr('data-container-width')
      , prevHeight = chart.attr('data-container-height')

    if (prevWidth == _dimensions._containerWidth && prevHeight == _dimensions._containerHeight) {
      let duration = isUpdating ? 0 : DURATION // If we were previously updating, we're going to immediately jump to the final state
      // Basic data update, size of the container didn't change
      chart.defaultTransition.duration(duration) // Use the default transition duration
      chart.yTransition.duration(duration) // Sets the y-axis transition to use our default transition
                                                     // This can be overridden by a specific chart
    } else {
      // This is a resize
      _svg.classed('is-updating', false) // Remove the is-updating flag, since this will happen immediately
    }

    chart.attr('data-container-width', _dimensions._containerWidth)
         .attr('data-container-height', _dimensions._containerHeight)
         .attr('data-max-columns', layout.columnCount())
         .transition(chart.defaultTransition)
         .attr('transform', 'translate(' + _dimensions.margin.left + ',' + _dimensions.margin.top + ')')

    chart.xAccessor = this._xAccessor
    chart.yAccessor = this._yAccessor
    chart.curve = this._curve
    chart.previous = _svg.node().__previous
    chart.ee = this._ee
    chart._data = data // Store original data
    chart._graphTypes = {}
    chart._scalers = _scalers

    let _hover = hover()
    if (this._hoverTooltipRows) {
      _hover.tooltipRows(this._hoverTooltipRows)
    }
    if (this._hoverContents) {
      _hover.contents(this._hoverContents)
    }
    this._hover = _hover
    chart._hover = _hover // So individual charts can hook into the hover object to attach their tooltips

    try {
      chart.call(plot, chart, this._xaxis, _scalers, _hover)
           .select('.axes')
           .call(yaxis(layout, _dimensions.width, _scalers.y, chart.yTransition))
    } catch (e) {
      if (e instanceof ChartDrawAbortError) {
        // Ignore. We aborted drawing for a reason
        return
      }
      throw e
    }


    chart.defaultTransition.end().then(() => {
      _svg.classed('is-updating', false)
      this.emit('transition-end')
    }).catch(() => {
      /* Interruptted */
      _svg.classed('is-updating', false)
    })

    // Store the layout data for next time, needed for animations
    _svg.node().__previous = {
      scalers: _scalers,
      layout,
      dimensions: _dimensions
    }

    return this
  }

  getLegend (type, dimensions, options = {}) {
    let graph = this._chart._graphTypes[type]

    if (graph.legend) {
      return graph.legend(this._chart, type, dimensions, options)
    } else {
      return null // No legend supported
    }
  }

  dimensions (arg) {
    if (!arguments.length) {
      return this._dimensions
    }
    this._dimensions = arg
    return this
  }

  xAccessor (arg) {
    if (!arguments.length) {
      return this._xAccessor
    }
    this._xAccessor = arg
    return this
  }

  yAccessor (arg) {
    if (!arguments.length) {
      return this._yAccessor
    }
    this._yAccessor = arg
    return this
  }

  xScaler (arg) {
    if (!arguments.length) {
      return this._xScaler
    }
    this._xScaler = arg
    return this
  }

  yScaler (arg) {
    if (!arguments.length) {
      return this._yScaler
    }
    this._yScaler = arg
    return this
  }

  xaxis (arg) {
    if (!arguments.length) {
      return this._xaxis
    }
    this._xaxis = arg
    return this
  }

  curve (arg) {
    if (!arguments.length) {
      return this._curve
    }
    this._curve = arg
    return this
  }

  hoverTooltipRows (arg) {
    if (!arguments.length) {
      return this._hoverTooltipRows
    }
    this._hoverTooltipRows = arg
    return this
  }

  hoverContents (arg) {
    if (!arguments.length) {
      return this._hoverContents
    }
    this._hoverContents = arg
    return this
  }

  destroy () {
    this.destroyTooltip()
    if (this.svg) {
      this.svg.remove()
    }
  }

  destroyTooltip () {
    if (!this._hover) {
      return
    }
    this._hover.destroy(this.svg)
  }

}

export default Chart
